<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('makerForm.detailsTitle') }}</h2>
            <p class="text-muted small mb-0">{{ maker.name || $t('makerForm.detailsTitle') }}</p>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 mt-2 mt-md-0">
          <router-link :to="{ name: 'EditMaker', params: { id: id } }" class="btn btn-primary px-3 py-2 mr-2 font-weight-bold">
            <i class="mdi mdi-lead-pencil mr-1"></i> {{ $t('common.edit') }}
          </router-link>
          <button type="button" class="btn btn-outline-danger px-3 py-2" @click="confirmDelete" :disabled="loading">
            <i class="mdi mdi-delete-outline mr-1"></i> {{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Profile Card -->
    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body">
          <div class="row">
            <!-- Left: Profile Info -->
            <div class="col-md-4 text-center border-right border-secondary pr-md-4 mb-4 mb-md-0">
              <div class="avatar-circle mx-auto mb-3" style="width: 96px; height: 96px; background: rgba(139, 92, 246, 0.15); color: #8b5cf6; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 36px; overflow: hidden; border: 2px solid #8b5cf6;">
                <img v-if="maker.profile_picture" :src="config.STORAGE_URL + maker.profile_picture" style="width: 100%; height: 100%; object-fit: cover;" />
                <span v-else class="font-weight-bold">{{ getInitials(maker.name) }}</span>
              </div>
              <h4 class="text-white font-weight-bold mb-1">{{ maker.name }}</h4>
              <p class="text-muted small mb-2">{{ maker.position || ($currentLocale === 'id' ? 'Manajer Tim' : 'Team Manager') }}</p>
              <div class="d-flex justify-content-center gap-2 mb-3">
                <span class="badge badge-info px-3 py-1 font-weight-medium">{{ $currentLocale === 'id' ? 'Manajer' : 'Manager' }}</span>
                <span class="badge badge-dark border border-secondary px-3 py-1 text-info font-weight-medium" v-if="maker.division">
                  <i class="mdi mdi-account-group mr-1"></i> {{ maker.division.name }}
                </span>
              </div>
              <div class="text-muted small">
                <i class="mdi mdi-calendar-check mr-1"></i> {{ $t('makerForm.joinDate') }}: {{ formatDate(maker.created_at) }}
              </div>
            </div>

            <!-- Right: Details & Contact -->
            <div class="col-md-8 pl-md-4">
              <h5 class="text-white font-weight-bold mb-3">{{ $currentLocale === 'id' ? 'Informasi Akun & Kontak Manajer' : 'Manager Account & Contact Information' }}</h5>
              <div class="row">
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('makerForm.emailAddress') }}</label>
                  <div class="text-white font-weight-medium">
                    <i class="mdi mdi-email-outline text-muted mr-1"></i>
                    {{ maker.email || '-' }}
                  </div>
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('makerForm.phoneNumber') }}</label>
                  <div class="text-white font-weight-medium">
                    <i class="mdi mdi-phone-outline text-muted mr-1"></i>
                    {{ maker.phone_number || '-' }}
                  </div>
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('makerForm.dateOfBirth') }}</label>
                  <div class="text-white font-weight-medium">
                    <i class="mdi mdi-cake-variant text-muted mr-1"></i>
                    {{ maker.dob ? formatDate(maker.dob) : '-' }}
                  </div>
                </div>
                <div class="col-sm-6 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('makerForm.squad') }}</label>
                  <div class="text-white font-weight-medium">
                    <i class="mdi mdi-account-multiple text-muted mr-1"></i>
                    {{ (maker.division && maker.division.name) || ($currentLocale === 'id' ? 'Belum Ditugaskan' : 'Unassigned') }}
                  </div>
                </div>
              </div>

              <hr class="border-secondary my-4" />

              <h5 class="text-white font-weight-bold mb-3">{{ $currentLocale === 'id' ? 'Wewenang Operasional Manajer' : 'Manager Operational Privileges' }}</h5>
              <div class="row g-3">
                <div class="col-sm-4 mb-2">
                  <div class="p-3 bg-dark rounded border border-secondary text-center">
                    <span class="text-muted small d-block mb-1">{{ $currentLocale === 'id' ? 'Rancang Misi' : 'Design Missions' }}</span>
                    <h5 class="text-success font-weight-bold mb-0 mt-1">
                      <i class="mdi mdi-check-decagram mr-1"></i> {{ $currentLocale === 'id' ? 'Diizinkan' : 'Authorized' }}
                    </h5>
                  </div>
                </div>
                <div class="col-sm-4 mb-2">
                  <div class="p-3 bg-dark rounded border border-secondary text-center">
                    <span class="text-muted small d-block mb-1">{{ $currentLocale === 'id' ? 'Audit Bukti Misi' : 'Audit Mission Proofs' }}</span>
                    <h5 class="text-info font-weight-bold mb-0 mt-1">
                      <i class="mdi mdi-shield-check mr-1"></i> {{ $currentLocale === 'id' ? 'Verifikator' : 'Verifier' }}
                    </h5>
                  </div>
                </div>
                <div class="col-sm-4 mb-2">
                  <div class="p-3 bg-dark rounded border border-secondary text-center">
                    <span class="text-muted small d-block mb-1">{{ $currentLocale === 'id' ? 'Akses Studio Manajer' : 'Manager Studio Access' }}</span>
                    <h5 class="text-warning font-weight-bold mb-0 mt-1">
                      <i class="mdi mdi-creation mr-1"></i> {{ $t('common.active') }}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { getDetailMaker, deleteMaker } from '@/services/makerService';
import config from '@/config/config';
import Swal from 'sweetalert2';

export default {
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      maker: {},
      config,
      loading: false,
    };
  },
  mounted() {
    this.fetchDetailMaker();
  },
  methods: {
    async fetchDetailMaker() {
      try {
        const response = await getDetailMaker(this.id);
        this.maker = response.data || {};
      } catch (err) {
        console.error('Error fetching maker detail:', err);
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMMM YYYY');
    },
    getInitials(name) {
      if (!name) return 'GM';
      return name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    back() {
      this.$router.go(-1);
    },
    confirmDelete() {
      Swal.fire({
        title: this.$t('makerForm.confirmDelete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.loading = true;
            await deleteMaker(this.id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$t('makerForm.deletedSuccess'),
              confirmButtonColor: '#3b82f6'
            });
            this.$router.push('/maker');
          } catch (err) {
            Swal.fire({
              icon: 'error',
              title: this.$t('common.error'),
              text: this.$currentLocale === 'id' ? 'Gagal menghapus Manajer' : 'Failed to delete Manager',
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },
  },
};
</script>
